import { Dialog, DialogContent } from '@mui/material'
import { SETTLEMENT_EXPORT_LIST_MAX_WINDOW_DAYS } from 'api/settlementExports/settlementExports.constants'
import type { SettlementExportRecord } from 'api/settlementExports/settlementExports.types'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import settlementExportStore from 'store/settlementExport/settlementExport'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import { isRequestedInDayRange, recentDayRange } from './SettlementExportButton.helper'
import type { DayRange } from './SettlementExportButton.types'
import { SettlementExportList } from './SettlementExportList'
import { SettlementExportRunForm } from './SettlementExportRunForm'

type SettlementExportDialogProps = {
  canRun: boolean
  initialRunRange: DayRange
  onClose: () => void
}

export function SettlementExportDialog({ canRun, initialRunRange, onClose }: SettlementExportDialogProps): ReactNode {
  const { t } = useTranslation()
  const [listRange, setListRange] = useState(() => recentDayRange(SETTLEMENT_EXPORT_LIST_MAX_WINDOW_DAYS))

  useEffect(() => {
    settlementExportStore.isDialogOpen = true
    return () => {
      settlementExportStore.isDialogOpen = false
    }
  }, [])

  // A range chosen when the dialog opened ends at that day's midnight, so an export created after
  // midnight, or while an older range is shown, would otherwise never appear in the list.
  function handleCreated(settlementExport: SettlementExportRecord | null): void {
    if (!settlementExport || !isRequestedInDayRange(listRange, settlementExport.created_at)) {
      setListRange(recentDayRange(SETTLEMENT_EXPORT_LIST_MAX_WINDOW_DAYS))
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <CustomDialogTitle title={t(`Settlement export`)} handleClose={onClose} />
      <DialogContent>
        <div className="flex flex-col gap-6">
          {canRun && <SettlementExportRunForm initialRange={initialRunRange} onCreated={handleCreated} />}
          <SettlementExportList range={listRange} onRangeChange={setListRange} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
